import logoSeapoint from '@/assets/images/logo-seapoint.svg';
import { useAnalytics } from '@/contexts/analytics-context/use-analytics';
import { Badge } from '@/design-system/badge/badge';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarLogo,
  SidebarLogoLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/design-system/sidebar';
import { Tooltip } from '@/design-system/tooltip/tooltip';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';
import { getNavItemContent } from '../utils/get-nav-item-content';
import AvatarDropdown from './avatar-dropdown';
import { SideNavItem } from './side-nav';

type UserInfo = {
  name?: string;
  email?: string;
  avatarUrl?: string;
};

type SideNavNewProps = {
  items: SideNavItem[];
  disabledItems: { item: SideNavItem; label: string }[];
  userInfo?: UserInfo;
  baseCurrency?: SupportedCountryCurrency;
  onCurrencyChange?: (currency: SupportedCountryCurrency) => void;
  onTeamClick?: () => void;
  onSwitchOrgClick?: () => void;
  onIntegrationsClick?: () => void;
  onLogout?: () => void;
};

type WorkflowSectionSideNavItem = Extract<SideNavItem, 'INVOICE_PAY' | 'PAYROLLS'>;

const workflowSectionSideNavItems: WorkflowSectionSideNavItem[] = ['INVOICE_PAY', 'PAYROLLS'];

export const SideNavNew = ({
  items,
  disabledItems,
  userInfo,
  baseCurrency,
  onCurrencyChange,
  onTeamClick,
  onSwitchOrgClick,
  onIntegrationsClick,
  onLogout,
}: SideNavNewProps) => {
  const navigate = useNavigate();
  const mainNavItems = items.filter((item) => !workflowSectionSideNavItems.some((i) => i === item));
  const workflowItems = items.filter((item) => workflowSectionSideNavItems.some((i) => i === item));

  const getDisabledConfig = (item: SideNavItem) => {
    const disabledItem = disabledItems.find((disabled) => disabled.item === item);
    return disabledItem ? { isDisabled: true as const, label: disabledItem.label } : { isDisabled: false as const };
  };

  const showFooter = userInfo && onLogout;

  return (
    <Sidebar collapsible="none" className="border-none bg-sidebar">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              onClick={() => {
                navigate('/');
              }}
            >
              <SidebarLogo>
                <img src={logoSeapoint} alt="Seapoint" />
              </SidebarLogo>
              <SidebarLogoLabel>
                <span className="truncate font-semibold">Seapoint</span>
              </SidebarLogoLabel>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup className="px-2 py-0">
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              {mainNavItems.map((item) => (
                <SidebarNavItem key={item} variant={item} disabledConfig={getDisabledConfig(item)} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {workflowItems.length > 0 && (
          <SidebarGroup className="px-2 py-0">
            <SidebarGroupLabel className="h-8 px-2 text-xs font-medium text-sidebar-foreground/70">
              Workflows
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-0.5">
                {workflowItems.map((item) => (
                  <SidebarNavItem key={item} variant={item} disabledConfig={getDisabledConfig(item)} />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      {showFooter && (
        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <AvatarDropdown
                avatarUrl={userInfo.avatarUrl}
                name={userInfo.name}
                email={userInfo.email}
                baseCurrency={baseCurrency}
                onCurrencyChange={onCurrencyChange}
                onTeamClick={onTeamClick ?? (() => {})}
                onSwitchOrgClick={onSwitchOrgClick ?? (() => {})}
                onIntegrationsClick={onIntegrationsClick ?? (() => {})}
                logout={onLogout}
              />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      )}
    </Sidebar>
  );
};

type SidebarNavItemProps = {
  variant: SideNavItem;
  disabledConfig: { isDisabled: true; label: string } | { isDisabled: false };
};

const SidebarNavItem = ({ variant, disabledConfig }: SidebarNavItemProps) => {
  const analytics = useAnalytics();
  const location = useLocation();
  const { icon: Icon, label, linkTo, isBeta } = getNavItemContent(variant);
  const isActive = location.pathname === linkTo || location.pathname.startsWith(`${linkTo}/`);

  if (disabledConfig.isDisabled) {
    return (
      <SidebarMenuItem>
        <Tooltip
          trigger={
            <SidebarMenuButton disabled className="h-9 cursor-not-allowed gap-2 px-2 opacity-50">
              <Icon size={16} />
              <span className="flex-1 truncate text-sm">{label}</span>
              {isBeta && (
                <Badge variant="discover" shape="pill" className="text-xs">
                  Beta
                </Badge>
              )}
            </SidebarMenuButton>
          }
          content={<p className="text-sm">{disabledConfig.label}</p>}
        />
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={!!isActive} tooltip={label} className="h-9 gap-2 px-2">
        <NavLink
          to={linkTo}
          onClick={() => {
            analytics.track('nav_item_clicked', { nav_item: label });
          }}
        >
          <Icon size={16} />
          <span className="flex-1 truncate text-sm">{label}</span>
          {isBeta && (
            <Badge variant="discover" shape="pill" className="text-xs">
              Beta
            </Badge>
          )}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};
